import React, { useState, useMemo, useEffect } from 'react';
import { Check, Plus, ChevronRight, ArrowRight, Star } from 'lucide-react';
import { toast } from 'sonner';
import { formatMoney } from '../../lib/formatMoney';
import { resolveProposalAddOns } from '../../lib/proposalAddOnVisibility';
import './ProposalEnhancements.css';
const API = process.env.REACT_APP_BACKEND_URL;
// The app's gold category icon set — thumbnail fallback when an add-on has no
// photo, so a card never renders a broken image.
const PEN_ICON_BASE = 'https://cdn.jsdelivr.net/gh/Mnabie01/staffed-assets@main/';
const PEN_ICON_FILE = {
  LIGHTING: 'lighting', VISUALS: 'visuals', TALENT: 'talent', PRODUCTION: 'production',
  'PHOTO OPS': 'photo-ops', FX: 'fx', EXTRAS: 'extras', DECOR: 'other', NOVELTY: 'other', OTHER: 'other',
};
// Category words → the gold icon set, so a photo-less add-on always shows its
// CATEGORY icon (lighting / photo / entertainment / effects / sound / visual)
// and never a generic placeholder.
const PEN_ICON_WORDS = [
  [/light|uplight|glow|neon/i, 'lighting'],
  [/photo|booth|selfie|camera|print/i, 'photo-ops'],
  [/entertain|talent|perform|dj|band|music|host|dancer|artist/i, 'talent'],
  [/fx|effect|spark|cold spark|haze|fog|smoke|confetti|co2|pyro/i, 'fx'],
  [/sound|audio|speaker|mic|pa\b|production|stage|rig/i, 'production'],
  [/visual|video|screen|led|projection|content|monitor/i, 'visuals'],
  [/extra|add|upgrade|package/i, 'extras'],
];
const penIcon = (cat) => {
  const raw = (typeof cat === 'string' ? cat.trim() : '');
  const k = raw ? raw.toUpperCase() : 'OTHER';
  const direct = PEN_ICON_FILE[k];
  if (direct && direct !== 'other') return PEN_ICON_BASE + direct + '.webp';
  const hit = raw && PEN_ICON_WORDS.find(([re]) => re.test(raw));
  return PEN_ICON_BASE + (hit ? hit[1] : (direct || 'other')) + '.webp';
};
const slug = (s, i) => String(s || `addon-${i}`).toLowerCase().replace(/[^a-z0-9]+/g, '-');
// "$500" / "$1,500" — never "$0" and never a second dollar sign on free text.
// A missing or zero price returns '' so the card simply omits its price line.
const priceText = (price, amount) => {
  const free = (price != null && String(price).trim()) ? String(price).trim() : '';
  const src = free || (amount != null ? amount : null);
  if (src == null || src === '') return '';
  const out = formatMoney(src, { decimals: 0 });
  if (!out || out === '$0') return '';
  return out;
};
const money = (n) => formatMoney(n, { decimals: 0 }); // eslint-disable-line no-unused-vars

const ProposalAddOns = ({ eventType, companyIndustry, clientToken, addOns, onAddOnInterest, onReview, onSelectionChange, readOnly = false, liveAddOns = [], hiddenKeys = [], featuredKeys = null, shownKeys = [] }) => {
  const [selected, setSelected] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [featOpen, setFeatOpen] = useState(false);
  // PRESENTATION LIVE, PRICE FROZEN — the resolver matches each shown add-on
  // against the live library for photo/benefit/category/popular/featured and
  // takes the quoted amount from the proposal's frozen snapshot. EVERY shown
  // add-on renders, priced or not; an add-on with no live match falls back to
  // its frozen snapshot copy so the card still appears.
  const catalog = useMemo(() => {
    const res = resolveProposalAddOns(liveAddOns, addOns, hiddenKeys, featuredKeys, shownKeys);
    const rows = res.items
      .filter((it) => it.shown)
      .map((item, i) => {
        const raw = item.raw || {};
        const included = (item.options || []).map((o, j) => o.name || `Option ${j + 1}`);
        return {
          key: item.id || slug(item.name, i),
          liveKeys: [item.id, item.addOnId].filter(Boolean).map(String),
          name: item.name,
          benefit: item.benefit || '',
          price: priceText(item.price, item.amount),
          amount: item.amount != null ? Number(item.amount) : null,
          photo: raw.photo || '',
          popular: !!raw.popular,
          featured: !!raw.featured,
          category: (raw.category || '').trim(),
          included,
        };
      });
    // Snapshot-only fallback: a shown add-on that has left the live library.
    const have = new Set(rows.flatMap((r) => r.liveKeys));
    const shownSet = new Set((Array.isArray(shownKeys) ? shownKeys : []).map(String));
    (Array.isArray(addOns) ? addOns : []).forEach((s, i) => {
      if (!s || !(s.name || s.title)) return;
      const sid = String(s.id || s.key || slug(s.name || s.title, i));
      if (!shownSet.has(sid) || have.has(sid)) return;
      have.add(sid);
      rows.push({
        key: sid,
        liveKeys: [sid],
        name: s.name || s.title,
        benefit: s.benefit || s.description || '',
        price: priceText(s.price, s.amount),
        amount: s.amount != null ? Number(s.amount) : null,
        photo: s.photo || '',
        popular: !!s.popular,
        featured: !!s.featured,
        category: (s.category || '').trim(),
        included: [],
      });
    });
    return rows;
  }, [addOns, liveAddOns, hiddenKeys, featuredKeys, shownKeys]);
  // The featured hero: first live-featured, shown, not-yet-added add-on. It
  // renders ONLY as the hero — excluded from its category list below so it is
  // never duplicated. Once added (or if none), the category list owns it again.
  const featured = useMemo(
    () => catalog.find((a) => a.featured && !selected.has(a.key)) || null,
    [catalog, selected],
  );
  const groups = useMemo(() => {
    const byCat = new Map();
    catalog.filter((a) => !featured || a.key !== featured.key).forEach((a) => {
      const cat = (a.category || '').trim();
      if (!byCat.has(cat)) byCat.set(cat, []);
      byCat.get(cat).push(a);
    });
    const cats = Array.from(byCat.keys()).sort((a, b) => {
      if (!a) return 1;
      if (!b) return -1;
      return a.toLowerCase() < b.toLowerCase() ? -1 : 1;
    });
    return cats.map((cat) => ({
      key: cat || '__more__',
      label: cat || 'More',
      items: byCat.get(cat).slice().sort((x, y) => (y.popular ? 1 : 0) - (x.popular ? 1 : 0)),
    }));
  }, [catalog, featured]);
  const addOne = (item) => {
    if (readOnly || selected.has(item.key)) return;
    setSelected((prev) => { const next = new Set(prev); next.add(item.key); return next; });
    if (clientToken && API) {
      fetch(`${API}/api/leads/client-view/${encodeURIComponent(clientToken)}/addon-interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: item.key, name: item.name, price: item.price }),
      }).catch(() => {});
    }
    if (typeof onAddOnInterest === 'function') onAddOnInterest(item);
    else toast.success(`${item.name} added — we'll include it in your contract.`);
  };
  const toggleExpand = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const addedItems = catalog.filter((a) => selected.has(a.key));
  const addedCount = addedItems.length;
  const addedTotal = addedItems.reduce((sum, a) => sum + (a.amount != null ? a.amount : 0), 0);
  // Hand the live selection up so the review step can list it.
  const selectionSig = addedItems.map((a) => a.key).join('|');
  useEffect(() => {
    if (typeof onSelectionChange === 'function') {
      onSelectionChange(addedItems.map((a) => ({ key: a.key, name: a.name, price: a.price, amount: a.amount })));
    }
  }, [selectionSig]); // eslint-disable-line react-hooks/exhaustive-deps
  const includedList = (item) => (item.included.length ? item.included : (item.benefit ? [item.benefit] : []));
  if (!catalog.length) return null;

  const renderCard = (item) => {
    const open = expanded.has(item.key);
    const isAdded = selected.has(item.key);
    return (
      <div
        key={item.key}
        className={"pen-card" + (open ? " pen-open" : "") + (isAdded ? " pen-added" : "")}
        data-testid={`proposal-addon-${item.key}`}
        data-selected={isAdded ? 'true' : 'false'}
      >
        <div className="pen-row" onClick={() => toggleExpand(item.key)} data-testid={`proposal-addon-${item.key}-detail-toggle`}>
          <div className="pen-thumb">
            {item.photo
              ? <img className="pen-photo" src={item.photo} alt={item.name} loading="lazy" decoding="async" />
              : <img className="pen-ico" src={penIcon(item.category || item.name)} alt="" />}
          </div>
          <div className="pen-main">
            {item.popular && <span className="pen-fav" data-testid={`proposal-addon-${item.key}-badge`}><Star className="w-3 h-3" fill="currentColor" strokeWidth={0} /> Guest favorite</span>}
            <div className="pen-name" data-testid={`proposal-addon-${item.key}-name`}>{item.name}<ChevronRight strokeWidth={2.4} /></div>
            {item.benefit && <div className="pen-benefit">{item.benefit}</div>}
            {item.price && <div className="pen-price" data-testid={`proposal-addon-${item.key}-price`}>{item.price}</div>}
          </div>
          {!readOnly && (
            <button
              type="button"
              className={"pen-add" + (isAdded ? " pen-is-added" : "")}
              onClick={(e) => { e.stopPropagation(); addOne(item); }}
              data-testid={`proposal-addon-${item.key}-cta`}
            >
              {isAdded ? <><Check strokeWidth={2.6} /> Added</> : <><Plus strokeWidth={2.6} /> Add</>}
            </button>
          )}
        </div>
        <div className="pen-detail"><div className="pen-detail-in">
          <div className="pen-inc-label">What&apos;s included</div>
          <ul className="pen-inc">
            {includedList(item).map((line, j) => (
              <li key={j}><Check strokeWidth={2.6} /><span>{line}</span></li>
            ))}
          </ul>
          {!readOnly && (
            <button
              type="button"
              className={"pen-cta" + (isAdded ? " pen-is-added" : "")}
              onClick={(e) => { e.stopPropagation(); addOne(item); }}
              data-testid={`proposal-addon-${item.key}-detail-cta`}
            >
              {isAdded ? <><Check strokeWidth={2.6} /> Added</> : <>Add to my experience{item.price ? ` · ${item.price}` : ''}</>}
            </button>
          )}
        </div></div>
      </div>
    );
  };

  return (
    <section className="pen" data-testid="proposal-addons" role="region" aria-label="Enhance your experience">
      <span className="pen-hair" />
      <div className="pen-col">
        <div className="pen-head">
          <div>
            <div className="pen-eyebrow">Enhance your experience</div>
            <h3 className="pen-title" data-testid="proposal-addons-title">Make it unforgettable</h3>
            <p className="pen-sub" data-testid="proposal-addons-subtitle">The little touches your guests will talk about long after the last song. Tap any item to see what&apos;s included.</p>
          </div>
          {addedCount > 0 && (
            <span className="pen-added-pill" data-testid="proposal-addons-added-pill"><Check strokeWidth={3} /> {addedCount} added</span>
          )}
        </div>

        {featured && (
          <>
            <div className="pen-feat-label">A little something extra</div>
            <div className={"pen-feat" + (featOpen ? " pen-open" : "")} data-testid="proposal-addons-featured">
              <div className="pen-feat-img">
                {featured.photo
                  ? <img src={featured.photo} alt={featured.name} loading="lazy" decoding="async" />
                  : <div className="pen-feat-fallback"><img src={penIcon(featured.category || featured.name)} alt="" /></div>}
              </div>
              <div className="pen-feat-body">
                <span className="pen-pill-em"><Star className="w-3 h-3" fill="currentColor" strokeWidth={0} /> Make an impression</span>
                <div className="pen-feat-title">{featured.name}</div>
                {featured.benefit && <div className="pen-feat-benefit">{featured.benefit}</div>}
                {featured.price && <div className="pen-price" data-testid="proposal-addons-featured-price">{featured.price}</div>}
                <button type="button" className="pen-feat-explore" onClick={() => setFeatOpen((v) => !v)} data-testid="proposal-addons-featured-toggle">
                  <ChevronRight strokeWidth={2.4} /> Explore this upgrade
                </button>
                <div className="pen-feat-detail"><div className="pen-detail-in">
                  <div className="pen-inc-label">What&apos;s included</div>
                  <ul className="pen-inc">
                    {includedList(featured).map((line, j) => (
                      <li key={j}><Check strokeWidth={2.6} /><span>{line}</span></li>
                    ))}
                  </ul>
                  {!readOnly && (
                    <button type="button" className="pen-cta" onClick={() => addOne(featured)} data-testid="proposal-addons-featured-cta">
                      Add to my experience{featured.price ? ` · ${featured.price}` : ''}
                    </button>
                  )}
                </div></div>
              </div>
            </div>
          </>
        )}

        {groups.map((group) => (
          <div key={group.key} data-testid={`proposal-addon-group-${group.label}`}>
            <div className="pen-cat" data-testid={`proposal-addon-group-heading-${group.label}`}>
              {group.label} <span className="pen-cat-n" data-testid={`proposal-addon-group-count-${group.label}`}>· {group.items.length} {group.items.length === 1 ? 'option' : 'options'}</span>
            </div>
            {group.items.map(renderCard)}
          </div>
        ))}
      </div>

      <div className="pen-bar" data-testid="proposal-addons-summary">
        <div className="pen-bar-top">
          <div>
            <div className="pen-bar-t">Your enhancements</div>
            <div className="pen-bar-s" data-testid="proposal-addons-summary-count">{addedCount} {addedCount === 1 ? 'item' : 'items'} selected</div>
          </div>
          <div className="pen-bar-total" data-testid="proposal-addons-summary-total">{formatMoney(addedTotal, { decimals: 0 }) || '$0'}</div>
        </div>
        <button
          type="button"
          className="pen-bar-btn"
          onClick={() => { if (typeof onReview === 'function') onReview(); }}
          data-testid="proposal-addons-review"
        >
          Review my experience <ArrowRight strokeWidth={2.6} />
        </button>
      </div>
    </section>
  );
};
export default ProposalAddOns;
export { ProposalAddOns };
